import Chip from '@mui/material/Chip';
import { useAccess } from 'auth/access';
import { companiesApi } from '../admin.api';
import ResourceManager, { ResourceColumn, ResourceField } from '../components/ResourceManager';

/**
 * The firm's own companies (V19) — the names a project is taken under and a quotation is
 * issued by. Admin only; the Add Project screen reads the active ones for its picker.
 *
 * A company with projects under it cannot be deleted (the API refuses); switching Active
 * off takes it off the picker while leaving those projects as they are.
 */
const columns: ResourceColumn[] = [
  { id: 'name', dataKey: 'name', label: 'Company Name', align: 'left', sortable: true },
  { id: 'contactNo1', dataKey: 'contactNo1', label: 'Contact No. 1', align: 'left' },
  { id: 'email', dataKey: 'email', label: 'Email', align: 'left' },
  { id: 'gstNo', dataKey: 'gstNo', label: 'GST No.', align: 'left' },
  { id: 'city', dataKey: 'city', label: 'City', align: 'left', sortable: true },
  {
    id: 'isActive',
    dataKey: 'isActive',
    label: 'Status',
    align: 'center',
    renderCell: (value) => (
      <Chip
        size='small'
        label={value === false ? 'Inactive' : 'Active'}
        color={value === false ? 'default' : 'success'}
        variant='outlined'
      />
    ),
  },
];

/** Shared with the Add Project screen's "+ Add", so a company added there is just as complete. */
export const companyFields: ResourceField[] = [
  { name: 'name', label: 'Company Name', required: true },
  { name: 'address', label: 'Address', type: 'textarea' },
  { name: 'contact_no1', label: 'Contact No. 1' },
  { name: 'contact_no2', label: 'Contact No. 2' },
  { name: 'email', label: 'Email' },
  { name: 'website', label: 'Website' },
  { name: 'gst_no', label: 'GST No.', helperText: '15 characters, e.g. 27ABCDE1234F1Z5' },
  { name: 'registration_no', label: 'Registration No.' },
  { name: 'city', label: 'City' },
  { name: 'state', label: 'State' },
  { name: 'country', label: 'Country', defaultValue: 'India' },
  { name: 'is_active', label: 'Active', type: 'boolean', defaultValue: true },
  // V26 — what this company's bills print.
  { name: 'bill_prefix', label: 'Bill Prefix', helperText: 'Starts each bill number, e.g. SCPL → SCPL/INV/MOTE/7/2025-26' },
  { name: 'registration_label', label: 'Registration Label', helperText: 'How the Registration No. is labelled on bills, e.g. "LLP no."' },
  { name: 'logo_file_id', label: 'Logo (bill letterhead)', type: 'image', helperText: 'PNG or JPG, wide; printed top-right on bills' },
  { name: 'signature_file_id', label: 'Signature & stamp', type: 'image', helperText: 'One image with both; printed above the signatory' },
  { name: 'signatory_name', label: 'Signatory Name', helperText: 'e.g. R. V. Shirke' },
  { name: 'signatory_regd_no', label: 'Signatory Registration', helperText: 'e.g. P. M. C. Registration No.: 181 (CAT. A)' },
  { name: 'bank_name', label: 'Bank Name' },
  { name: 'bank_branch', label: 'Bank Branch' },
  { name: 'bank_account_no', label: 'Bank Account Number' },
  { name: 'bank_ifsc', label: 'Bank IFSC' },
];

export default function CompanyMasterPage() {
  // Only admin may save companies (the API refuses everyone else), so others get a read-only list.
  const admin = !!useAccess()?.admin;
  return (
    <ResourceManager
      title='COMPANY MASTER'
      entityName='Company'
      client={companiesApi}
      columns={columns}
      fields={companyFields}
      canCreate={admin}
      canEdit={admin}
      canDelete={admin}
    />
  );
}
